import clsx from "clsx";
import type { LucideIcon } from "lucide-react";
import { Icon, type IconSize } from "../Icon/Icon.js";
import { Spinner } from "../Spinner/Spinner.js";
import styles from "./IconButton.module.scss";

/** Box rungs, taken from what the codebase actually uses: 20 (a tag's ×) · 24 · 34 (the common modal-close
 * and row-action size) · 44 (touch targets). No in-between values, same discipline as `IconSize`.
 *
 * `field` is the odd one out and deliberately so: it is not a number but `--ac-control-h`, the height of a
 * single-line control. For an icon button standing IN a control row — beside a picker, an input, another
 * field — where a fixed rung is 6px over on a fine pointer and sets the row's height instead of fitting
 * inside it. Same rung `Button` has, for the same reason, and mobile-safe because the token is 44px there. */
export type IconButtonSize = "xs" | "sm" | "md" | "lg" | "field";

/** `danger` tints the hover for destructive actions; `invert` carries a scrim fill AT REST, for a control
 * floating over imagery or a filled tile; `outline` is the SHELL TOPBAR chip — a hairline ring and a paper
 * fill at rest, the border warming on hover. It exists because the topbar sits on a frosted translucent bar
 * where a borderless control reads as a bare glyph with no target, and the artist shell had hand-rolled
 * exactly this treatment (with its hover animating `border-color`) before the primitive could serve it. */
export type IconButtonTone = "default" | "danger" | "invert" | "accent" | "outline";

export interface IconButtonProps {
  icon: LucideIcon;
  /** REQUIRED. An icon-only button with no accessible name is a dead end for assistive tech; all 31 of the
   * hand-rolled ones carried a name and this keeps that true by construction. */
  label: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  size?: IconButtonSize;
  /** Override the glyph size the rung picks (16 for xs/sm, 20 for md/lg). */
  iconSize?: IconSize;
  shape?: "circle" | "square";
  /** `danger` tints the hover — for destructive row actions (remove, unfriend, delete);
   * `accent` carries the brand fill at rest — the CREATE affordance in a section head. */
  tone?: IconButtonTone;
  disabled?: boolean;
  /** ADDITIVE loading state, mirroring {@link Button}'s: the glyph is replaced by a spinner in the SAME
   * centred box (so the control does not resize or shift), and the button is disabled while it runs. It
   * exists because an icon-only SUBMIT that gives no feedback in flight is a button you press twice — there
   * is no label to go quiet, so the press has nothing to show for itself. */
  loading?: boolean;
  type?: "button" | "submit";
  title?: string;
  className?: string;
  /**
   * Popup-trigger semantics, for an icon button that OPENS something — the notifications bell's panel, a
   * disclosure. Without these an icon-only trigger announces as a plain button and gives no expanded state,
   * which is what pushed every such caller back to a hand-rolled `<button>` (and is how the bell came to
   * carry its own markup in three shells).
   */
  "aria-haspopup"?: "dialog" | "menu" | "listbox" | "true";
  "aria-expanded"?: boolean;
  /** A handle on the underlying `<button>`, for the callers that must RESTORE focus to this control
   * after something it opened unmounts (ending an inline edit unmounts the field the caret is in, so
   * without this the browser drops focus to `<body>`). React 19 takes `ref` as an ordinary prop, so
   * this needs no `forwardRef`; it is an opt-in escape hatch, not a reason to reach past the API. */
  ref?: React.Ref<HTMLButtonElement>;
}

const GLYPH: Record<IconButtonSize, IconSize> = { xs: 16, sm: 16, md: 20, lg: 20, field: 16 };

/**
 * An icon-only button: a sized, centred, focus-ringed box around one glyph.
 *
 * **Why this is a primitive.** Every icon button in the app hand-rolled the same eight declarations —
 * `display: inline-grid; place-items: center; width; height; padding: 0; border: none; border-radius;
 * background: transparent; cursor: pointer` — 31 of them across 29 files, under a dozen different class names
 * (`close`, `iconBtn`, `chipRemove`, `remove`, `unfriendBtn`, `dockButton`…). One of those declarations,
 * `padding: 0`, is easy to forget and fails quietly: a `<button>`'s UA padding shrinks the content box below
 * the icon, which then overflows rightward and sits off-centre inside a box that is still perfectly round.
 * That bug shipped in six of them (measured 2026-08-18).
 *
 * So this owns the geometry, and `label` is required so an icon-only control cannot ship nameless.
 *
 * It is deliberately NOT `Button`: that one carries a label, variants, and the `xs`-on-desktop rule. This is
 * the glyph-only affordance — a different control with a different contract.
 */
export function IconButton({
  icon,
  label,
  onClick,
  size = "md",
  iconSize,
  shape = "circle",
  tone = "default",
  disabled,
  loading = false,
  type = "button",
  title,
  className,
  ref,
  "aria-haspopup": haspopup,
  "aria-expanded": expanded,
}: IconButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(styles.button, className)}
      data-size={size}
      data-glyph={iconSize === undefined ? "auto" : undefined}
      data-shape={shape}
      data-tone={tone}
      aria-label={label}
      aria-haspopup={haspopup}
      aria-expanded={expanded}
      title={title}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
    >
      {loading ? (
        <Spinner size={iconSize ?? GLYPH[size]} />
      ) : (
        <Icon icon={icon} size={iconSize ?? GLYPH[size]} />
      )}
    </button>
  );
}
